import { useRef, useState } from "react";
import { isSafeUrl, type DataSource, type FieldSchema, type FieldType } from "@broadpaper/core";
import { Button, TextInput } from "../../ui/primitives.js";
import { Popover } from "../../ui/floating.js";
import { DataPicker } from "../DataPicker.js";
import { BindingField } from "./BindingField.js";
import { useOptions } from "../../state/context.js";

/**
 * How large a file may be before the built-in reader refuses it.
 *
 * A `data:` URL is not stored somewhere and referenced — it *is* the value, so
 * it is copied into every save, every export and every render request for the
 * life of the template, and base64 adds a third again on top. A megabyte is
 * generous for the things this path is for (a logo, a signature, a letterhead
 * rule) and small enough that a template full of them is still a document
 * rather than an archive. Hosts that need more supply `onUploadImage` and put
 * the file somewhere of their own.
 */
export const MAX_INLINE_IMAGE_BYTES = 1_000_000;

/** The formats `isSafeUrl` will accept back as a data URL, so nothing else is worth offering. */
const ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml";

const BINDABLE: FieldType[] = ["image", "url", "string"];

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsDataURL(file);
  });
}

function describeSize(bytes: number): string {
  return bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

/**
 * The `image` inspector field: an address, a picker to bind it to data, and a
 * way to put a file there.
 *
 * The upload is deliberately not a separate mode. Whatever route the value
 * arrived by it is still just a URL in the template, which is what keeps the
 * renderers, the validator and the PDF backends from needing to know that
 * uploading exists at all.
 */
export function ImageField({
  value,
  onChange,
  allowBinding,
  dataSources,
  scopeVars,
  onError
}: {
  value: string;
  onChange: (v: string) => void;
  allowBinding: boolean;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  onError: (message: string | null) => void;
}) {
  const options = useOptions();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const bound = /^\{\{[\s\S]*\}\}$/.test(value.trim());

  async function accept(file: File) {
    onError(null);
    setBusy(true);
    try {
      const upload = options.onUploadImage;
      if (upload) {
        const url = await upload(file);
        if (url) onChange(url);
        return;
      }
      if (file.size > MAX_INLINE_IMAGE_BYTES) {
        onError(
          `${describeSize(file.size)} is too large to store in the template. Use an image under ${describeSize(MAX_INLINE_IMAGE_BYTES)}, or link to one by address.`
        );
        return;
      }
      const url = await readAsDataUrl(file);
      // The same check the renderers and the validator apply, run here so the
      // refusal arrives while the file picker is still in mind rather than as
      // a blank space in the PDF. It is what rejects an SVG carrying a script.
      if (!isSafeUrl(url, { allowData: true })) {
        onError("That file cannot be used: it is not an image this designer accepts, or it contains scripting.");
        return;
      }
      onChange(url);
    } catch (err) {
      onError(err instanceof Error && err.message ? err.message : "The image could not be uploaded.");
    } finally {
      setBusy(false);
      // Cleared so choosing the same file twice in a row still fires a change.
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="bpe-binding bpe-image">
      {bound ? (
        <BindingField
          value={value.replace(/^\{\{\s*|\s*\}\}$/g, "")}
          onChange={(v) => onChange(v ? `{{ ${v} }}` : "")}
          dataSources={dataSources}
          scopeVars={scopeVars}
          types={BINDABLE}
        />
      ) : (
        <TextInput value={value} onChange={onChange} lazy placeholder="https://… or upload" />
      )}

      {/* Not rendered by the Button: a file input is the only way to open the
          picker, and a visually hidden one keeps the keyboard path intact. */}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="bpe-image__file"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void accept(file);
        }}
      />
      {!bound ? (
        <Button
          size="sm"
          variant="ghost"
          icon="upload"
          disabled={busy}
          tooltip={options.onUploadImage ? "Upload an image" : "Upload an image (stored in the template)"}
          onClick={() => fileRef.current?.click()}
        />
      ) : null}

      {allowBinding ? (
        <Popover className="bpe-picker" trigger={<Button size="sm" variant="ghost" icon="braces" tooltip="Bind to a field" />}>
          {(close) => (
            <DataPicker
              dataSources={dataSources}
              scopeVars={scopeVars}
              types={BINDABLE}
              onPick={(expr) => {
                onChange(`{{ ${expr} }}`);
                close();
              }}
            />
          )}
        </Popover>
      ) : null}
    </div>
  );
}
